import { BulkBar, Button, type ButtonVariant } from '@platform/design-system';
import { useAuth } from '@platform/auth-client';
import { Check, CircleX, Printer, Truck } from 'lucide-react';
import type { ReactNode } from 'react';
import { ORDERS_WRITE_ROLE } from '../customers/customers.ts';
import { usePageContext } from '../usePageContext.ts';
import { eligibleFor, type BulkKind } from './bulk.ts';
import { ORDERS_CONFIRM_ROLE, ORDERS_DISPATCH_ROLE, type Order } from './orders.ts';

export type OrderBulkBarProps = {
  selected: readonly Order[];
  busy: boolean;
  onAction: (kind: BulkKind) => void;
  onClear: () => void;
};

type BulkButton = {
  readonly kind: BulkKind;
  readonly role: string;
  readonly icon: ReactNode;
  readonly label: string;
  readonly variant: ButtonVariant;
};

export const OrderBulkBar = ({ selected, busy, onAction, onClear }: OrderBulkBarProps) => {
  const { t } = usePageContext();
  const auth = useAuth();
  const buttons: readonly BulkButton[] = [
    {
      kind: 'dispatch',
      role: ORDERS_DISPATCH_ROLE,
      icon: <Truck size={16} aria-hidden="true" />,
      label: t('orderBulk.dispatch'),
      variant: 'accent',
    },
    {
      kind: 'labels',
      role: ORDERS_DISPATCH_ROLE,
      icon: <Printer size={16} aria-hidden="true" />,
      label: t('orderBulk.printLabels'),
      variant: 'inverse',
    },
    {
      kind: 'confirm',
      role: ORDERS_CONFIRM_ROLE,
      icon: <Check size={16} aria-hidden="true" />,
      label: t('orderBulk.confirm'),
      variant: 'inverse',
    },
    {
      kind: 'cancel',
      role: ORDERS_WRITE_ROLE,
      icon: <CircleX size={16} aria-hidden="true" />,
      label: t('orderBulk.cancel'),
      variant: 'danger',
    },
  ];
  return (
    <BulkBar
      label={t('orderBulk.label')}
      countLabel={t('orderBulk.selected', { count: selected.length })}
      clearLabel={t('orderBulk.clear')}
      onClear={onClear}
    >
      {buttons
        .filter((button) => auth.hasRole(button.role))
        .map((button) => (
          <Button
            key={button.kind}
            variant={button.variant}
            size="sm"
            icon={button.icon}
            disabled={busy || eligibleFor(button.kind, selected).length === 0}
            onClick={() => {
              onAction(button.kind);
            }}
          >
            {button.label}
          </Button>
        ))}
    </BulkBar>
  );
};
